import { useEffect, useState } from "react";
import { api } from "./api";

/**
 * SyncPage — preview-only utility to push CMS data + media files to
 * production in two phases:
 *   Phase 1 — CMS data (pages, SEO, settings, scripts, blog,
 *             showcase, media metadata). Small payload, one POST.
 *   Phase 2 — Media file bytes. One POST per file so the bundle stays
 *             under Cloudflare's edge upload limit.
 *
 * Pre-flight: pings production to confirm the new sync endpoints are
 * actually deployed there. If production is still running OLDER
 * code, the admin sees a clear "redeploy needed" message instead of
 * a cryptic 502.
 *
 * Safety: every push automatically takes a `pre-push` snapshot of
 * preview's state first — see /admin/backups to roll back.
 */
export default function SyncPage() {
  const [confirmText, setConfirmText] = useState("");
  const [includeMedia, setIncludeMedia] = useState(true);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState("");
  const [cmsResult, setCmsResult] = useState(null);
  const [mediaResult, setMediaResult] = useState(null);
  const [error, setError] = useState("");
  const [prodStatus, setProdStatus] = useState(null);
  const [checkingProd, setCheckingProd] = useState(false);

  const armed = confirmText.trim().toUpperCase() === "PUSH";
  const prodReady = prodStatus && prodStatus.ready === true;

  const checkProduction = async () => {
    setCheckingProd(true);
    setError("");
    const startedAt = Date.now();
    try {
      const s = await api.syncCheckProduction();
      // Hold the spinner state for at least 600ms so the user
      // visually registers that the check actually happened.
      const elapsed = Date.now() - startedAt;
      if (elapsed < 600) {
        await new Promise((r) => setTimeout(r, 600 - elapsed));
      }
      setProdStatus({ ...s, checked_at: new Date().toISOString() });
    } catch (e) {
      setProdStatus({
        reachable: false,
        reason: e.message || "Check failed.",
        checked_at: new Date().toISOString(),
      });
    } finally {
      setCheckingProd(false);
    }
  };

  useEffect(() => {
    checkProduction();
  }, []);

  const handlePush = async () => {
    if (!armed || busy || !prodReady) return;
    setBusy(true);
    setError("");
    setCmsResult(null);
    setMediaResult(null);
    try {
      setPhase("cms");
      const cms = await api.syncPush();
      setCmsResult(cms);
      if (includeMedia) {
        setPhase("media");
        const media = await api.syncPushMedia();
        setMediaResult(media);
      }
      setConfirmText("");
    } catch (e) {
      setError(e.message || "Sync failed.");
    } finally {
      setPhase("");
      setBusy(false);
    }
  };

  const phaseLabel =
    phase === "cms"
      ? "Pushing CMS data…"
      : phase === "media"
      ? "Pushing media files (one by one)…"
      : "Push to Production →";

  return (
    <div data-testid="admin-sync-page">
      <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E]">
        Sync
      </div>
      <h1 className="mt-3 font-display text-4xl font-black tracking-[-0.025em] leading-[1.05]">
        Push to Production
      </h1>
      <p className="mt-5 max-w-2xl text-[0.95rem] text-neutral-400 leading-[1.65]">
        Bundles every CMS collection on this preview environment — pages,
        SEO, settings, scripts, blog, showcase, and the media library —
        and replaces the corresponding data on the live site. A safety
        snapshot of preview is taken automatically before each push (see{" "}
        <a href="/admin/backups" className="text-[#F4D64E] underline">
          Backups
        </a>
        ).
      </p>

      {/* Pre-flight status */}
      <div
        data-testid="admin-sync-prod-status"
        className={`mt-8 max-w-2xl rounded-md border px-5 py-4 transition-colors ${
          checkingProd
            ? "border-[#F4D64E]/30 bg-[#F4D64E]/[0.05]"
            : prodReady
            ? "border-emerald-500/30 bg-emerald-500/[0.06]"
            : "border-amber-500/30 bg-amber-500/[0.06]"
        }`}
      >
        <div className="flex items-center justify-between gap-4">
          <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase flex items-center gap-2">
            {checkingProd ? (
              <>
                <span className="inline-block w-2 h-2 rounded-full bg-[#F4D64E] animate-pulse" />
                Checking production…
              </>
            ) : prodReady ? (
              "✓ Production ready"
            ) : (
              "⚠ Production not ready"
            )}
          </div>
          <button
            type="button"
            onClick={checkProduction}
            disabled={checkingProd}
            data-testid="admin-sync-recheck-btn"
            className={`text-[0.62rem] font-bold tracking-[0.18em] uppercase transition-colors ${
              checkingProd
                ? "text-neutral-500 cursor-wait"
                : "text-neutral-400 hover:text-white"
            }`}
          >
            {checkingProd ? "Checking…" : "Re-check"}
          </button>
        </div>
        {prodStatus && !checkingProd && (
          <div className="mt-2 text-[0.85rem] text-neutral-300 leading-[1.5]">
            {prodStatus.message || prodStatus.reason}
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[0.7rem] text-neutral-500">
              {prodStatus.production_url && (
                <span className="break-all">{prodStatus.production_url}</span>
              )}
              {prodStatus.health_status !== undefined && (
                <span>health: HTTP {prodStatus.health_status}</span>
              )}
              {prodStatus.checked_at && (
                <span>checked: {new Date(prodStatus.checked_at).toLocaleTimeString()}</span>
              )}
            </div>
          </div>
        )}
      </div>

      <div
        className={`mt-6 max-w-2xl bg-[#0F141A] border border-white/[0.06] rounded-xl p-7 ${
          prodReady ? "" : "opacity-60"
        }`}
      >
        <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
          Options
        </div>
        <label className="mt-4 flex items-center gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeMedia}
            onChange={(e) => setIncludeMedia(e.target.checked)}
            data-testid="admin-sync-media-toggle"
            className="w-4 h-4 accent-[#F4D64E]"
          />
          <span className="text-[0.9rem] text-neutral-300">
            Also push media files (slower — may take a minute or two for
            large libraries)
          </span>
        </label>

        <div className="mt-7 text-[0.62rem] font-bold tracking-[0.3em] uppercase text-neutral-500">
          Confirm action
        </div>
        <div className="mt-3 text-[0.9rem] text-neutral-300 leading-[1.6]">
          This will <span className="text-[#F4D64E] font-bold">overwrite</span>{" "}
          CMS data on production. Type{" "}
          <code className="px-1.5 py-0.5 rounded bg-white/[0.06] text-[#F4D64E] font-bold">
            PUSH
          </code>{" "}
          below to enable the button.
        </div>
        <input
          type="text"
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="Type PUSH to confirm"
          data-testid="admin-sync-confirm-input"
          className="mt-5 w-full bg-[#0B0F14] border border-white/[0.08] focus:border-[#F4D64E]/40 focus:outline-none rounded-md px-4 py-3 text-[0.9rem] tracking-[0.05em] uppercase"
        />
        <button
          type="button"
          onClick={handlePush}
          disabled={!armed || busy || !prodReady}
          data-testid="admin-sync-push-btn"
          className={`mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-md text-[0.78rem] font-bold tracking-[0.18em] uppercase transition-colors ${
            armed && !busy && prodReady
              ? "bg-[#F4D64E] text-neutral-950 hover:bg-[#FFE36B]"
              : "bg-white/[0.06] text-neutral-500 cursor-not-allowed"
          }`}
        >
          {busy ? phaseLabel : "Push to Production →"}
        </button>
      </div>

      {error && (
        <div
          data-testid="admin-sync-error"
          className="mt-6 max-w-2xl rounded-md border border-red-500/30 bg-red-500/[0.06] px-5 py-4 text-[0.85rem] text-red-300"
        >
          {error}
        </div>
      )}

      {cmsResult && (
        <ResultPanel
          title="CMS data pushed"
          subtitle={cmsResult.production_url}
          stats={cmsResult.pushed}
          testId="admin-sync-result-cms"
          backupId={cmsResult.pre_push_backup_id}
        />
      )}

      {mediaResult && (
        <ResultPanel
          title="Media files pushed"
          subtitle={mediaResult.production_url}
          stats={{
            sent: mediaResult.sent,
            skipped: mediaResult.skipped,
            failed: (mediaResult.failed || []).length,
            total: mediaResult.total_attempted,
          }}
          testId="admin-sync-result-media"
          failures={mediaResult.failed}
        />
      )}
    </div>
  );
}

function ResultPanel({ title, subtitle, stats, testId, failures, backupId }) {
  return (
    <div
      data-testid={testId}
      className="mt-6 max-w-2xl rounded-md border border-emerald-500/30 bg-emerald-500/[0.06] px-5 py-5"
    >
      <div className="text-[0.62rem] font-bold tracking-[0.3em] uppercase text-emerald-400">
        ✓ {title}
      </div>
      <div className="mt-2 text-[0.85rem] text-neutral-300 break-all">
        Pushed to: <span className="font-bold text-white">{subtitle}</span>
      </div>
      {backupId && (
        <div className="mt-1 text-[0.75rem] text-neutral-500">
          Pre-push safety backup: <span className="font-mono">{backupId}</span>
        </div>
      )}
      <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-3">
        {Object.entries(stats || {}).map(([k, v]) => (
          <div
            key={k}
            className="rounded-md bg-white/[0.04] border border-white/[0.06] px-4 py-3"
          >
            <div className="text-[0.6rem] font-bold tracking-[0.2em] uppercase text-neutral-500">
              {k.replace(/_/g, " ")}
            </div>
            <div className="mt-1 font-display text-2xl font-black tracking-[-0.02em] tabular-nums">
              {v}
            </div>
          </div>
        ))}
      </div>
      {failures && failures.length > 0 && (
        <div className="mt-5 rounded-md bg-amber-500/[0.08] border border-amber-500/30 px-4 py-3">
          <div className="text-[0.62rem] font-bold tracking-[0.25em] uppercase text-amber-300">
            {failures.length} failed
          </div>
          <ul className="mt-2 text-[0.78rem] text-amber-200/90 leading-[1.6] list-disc pl-4">
            {failures.slice(0, 10).map((f, i) => (
              <li key={i} className="break-all">
                {f}
              </li>
            ))}
            {failures.length > 10 && (
              <li className="text-amber-300/80">
                …and {failures.length - 10} more
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
